import React from 'react';
import styled from 'styled-components';
import {Avatar, Icon, SubHeading, Text, Tooltip, WorkSignifier, tokensForThemeProvider} from '@forecast-it/design-system';
import {CellValue, RagState} from '../../data/reportRowModel';
import {formatDuration} from '../durationFormat';

const tokens = tokensForThemeProvider.sky.sys;

/** Icon glyph + DS icon-color token per RAG state, matching the icon choice `RagStatusMessage`
 * itself uses internally (RagStatusMessage.js `getIcon`) — green/checkCircle, amber/warning,
 * red/alert — but WITHOUT that component's bordered-box chrome (see `StatusCellWrapper` below). */
const RAG_ICON: Record<RagState, {icon: string; color: string}> = {
	on_track: {icon: 'checkCircle', color: 'success'},
	at_risk: {icon: 'warning', color: 'warning'},
	off_track: {icon: 'alert', color: 'error'},
};

const NotAvailable = (): JSX.Element => (
	<Text size={2} color="secondary">
		N/A
	</Text>
);

/** the design renders the task status cell as plain inline text + icon — no
 * border, no box, natural (shrink-to-fit) width. The DS `RagStatusMessage` component always draws
 * a colored bordered box around its content (`RagStatusMessageWrapper` in RagStatusMessage.js) and
 * has no compact/inline variant (RagStatusMessage.d.ts exposes only `status`/`children`/
 * `iconOnLeft`), so it does not match this design — reusing it here would mean re-overriding its
 * own chrome via CSS overrides, which is worse than composing the same DS primitives (`Icon` +
 * `Text`) the rest of this file already uses. `inline-flex` keeps icon+label on one line at their
 * natural width instead of stretching to the table cell. */
const StatusCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

/** the design's project cell is the colored id pill (`WorkSignifier`) followed by the project
 * name. `WorkSignifier` renders only the id chip inline (name is its hover title), so the visible
 * name is a `Text` beside it. `inline-flex` keeps pill + name on one line at natural width. */
const ProjectCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

const ProjectCell = ({
	id,
	name,
	projectKey,
	color,
}: {
	id: string;
	name: string;
	projectKey: string;
	color: string;
}): JSX.Element => (
	<ProjectCellWrapper>
		<WorkSignifier workId={id} name={`${projectKey} ${name}`.trim()} color={color} />
		{/* The design renders the project name in the emphasised subheading/1 (16px, weight 500),
		    not the body/2 the other data cells use. */}
		{name ? (
			<Tooltip content={name} interactable={false}>
				<SubHeading size={1} ellipsis>
					{name}
				</SubHeading>
			</Tooltip>
		) : null}
	</ProjectCellWrapper>
);

const PersonsCell = ({persons}: {persons: {id: string; name: string; avatarUrl?: string}[]}): JSX.Element => (
	<>
		{persons.map((person, idx) => (
			<span
				key={person.id || `${person.name}-${idx}`}
				style={{display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8}}
			>
				<Avatar name={person.name} src={person.avatarUrl} size="s" />
				<Text size={2}>{person.name}</Text>
			</span>
		))}
	</>
);

export const CellRenderer = ({value}: {value: CellValue | undefined}): JSX.Element => {
	if (value === undefined || value.kind === 'na') {
		return <NotAvailable />;
	}

	switch (value.kind) {
		case 'text':
			return (
				<Text size={2} ellipsis tooltipOnEllipsis>
					{value.value}
				</Text>
			);
		case 'number':
			return <Text size={2}>{value.value}</Text>;
		case 'date':
			return <Text size={2}>{value.value}</Text>;
		case 'duration':
			return <Text size={2}>{formatDuration(value.minutes, value.unit)}</Text>;
		case 'project':
			return <ProjectCell id={value.id} name={value.name} projectKey={value.projectKey} color={value.color} />;
		case 'status': {
			const {icon, color} = RAG_ICON[value.rag];
			return (
				<StatusCellWrapper>
					<Text size={2}>{value.label}</Text>
					<Icon icon={icon} size={'s'} color={color} />
				</StatusCellWrapper>
			);
		}
		case 'persons':
			return value.persons.length === 0 ? <NotAvailable /> : <PersonsCell persons={value.persons} />;
		case 'tag':
			return value.tags.length === 0 ? (
				<NotAvailable />
			) : (
				<Text size={2}>{value.tags.map(tag => tag.name).join(', ')}</Text>
			);
		default:
			return <NotAvailable />;
	}
};
